import { useTranslation } from 'react-i18next';
import { useSyncStatus } from '../../services/complianceApi';
import { syncAge, SYNC_STALE_MINUTES } from './syncAge';

// STX-PROD-03 — the header's "CRM sync · 7m ago", ported from ptc-core's SyncStatusIndicator.
// Filings staff complete in the CRM reach this app ONLY through the 15-minute inbound sync, so a
// stall used to be invisible on every screen. Rules carried over from the reference:
//   - anything unknown (fetch error, no clean poll yet, an unparseable time) reads "unavailable",
//     never fresh;
//   - stale past SYNC_STALE_MINUTES (4 missed polls — the same hour the liveness alert uses);
//   - the sync switched off renders nothing: there is no freshness to claim;
//   - while the first answer is in flight it renders nothing (not "unavailable": nothing has failed).

export default function SyncFreshness() {
  const { t, i18n } = useTranslation();
  const { data, isError } = useSyncStatus();

  if (data && !data.inboundTaskSync.enabled) return null;
  // Still asking: say nothing yet. "Unavailable" is for a status that could not be read, not one not yet read;
  // flashing it on every page load told staff the sync was broken when it wasn't.
  if (!data && !isError) return null;

  const at = data?.inboundTaskSync.lastSucceededAt ?? null;
  const age = at ? syncAge(at, Date.now()) : null;
  if (isError || !data || !at || !age) {
    return <span className="sync-freshness" data-testid="sync-freshness-unavailable">{t('syncFreshness.unavailable')}</span>;
  }

  const ageText = t(`syncFreshness.age.${age.unit}`, { count: age.count });
  const stale = age.minutes > SYNC_STALE_MINUTES;
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const when = new Date(at).toLocaleString(locale, { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' });

  return (
    <span
      role="status"
      data-testid="sync-freshness"
      data-stale={stale}
      className={stale ? 'sync-freshness sync-freshness--stale' : 'sync-freshness'}
      title={t('syncFreshness.title', { at: when })}
    >
      {stale ? t('syncFreshness.stale', { age: ageText }) : t('syncFreshness.fresh', { age: ageText })}
    </span>
  );
}
